import { Fragment, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import Table1 from "src/components/ui/table1.component";
import { Transition } from "@headlessui/react";
import { usePagination } from "src/hooks/usePagination";
import Button from "../../components/Button";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { getMasterEscalationList, getRemarkHeader } from "src/services/invoice-api";
import arrowDown from "src/assets/arrow-down.svg";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select.component";
import { searchOptionsEscalate } from "src/pages/invoice-master/invoice-helper";
import { Input } from "src/components";
import FilterAccordion from "src/container/filter-accordion.component";
import filterIcon from "src/assets/filterIcon.svg";
import { DayPicker } from "react-day-picker";
import InvoiceApprovalDetails from "../InvoiceApprovalTab/InvoiceApprovalCards/InvoiceApprovalDetails";
import { getAllCompanyDetails } from "src/services/company-api";

const EscalatedMasterTab = () => {
    const SORT_OPTIONS = [
        { key: "ASC", value: "Ascending" },
        { key: "DESC", value: "Descending" },
    ];
    const STATUS_OPTIONS = [
        { key: "pending", value: "Pending" },
        { key: "resolved", value: "Resolved" },
        { key: "rejected", value: "Rejected" },
        { key: "on hold", value: "On Hold" },
    ];

    const [loading, setLoading] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [companyDetails, setCompanyDetails] = useState<any>();
    const [remarkHdr, setRemarkHdr] = useState<any>([]);
    const { pagination, onPaginationChange, pageIndex, pageSize, resetPagination } = usePagination();
    const [globalSearch, setGlobalSearch] = useState("");
    const [totalRowCount, setTotalRowCount] = useState(10);
    const [isFiltersShown, setIsFiltersShown] = useState<boolean>(false);
    const [searchCategory, setSearchCategory] = useState(searchOptionsEscalate[0].value);
    const [filters, setFilters] = useState<any>({
        status: [],
        orderBy: [],
        company: [],
        remarkType: [],
        dateRange: {
            from: undefined,
            to: undefined,
        },
    });


    const handleDateRangeChange = (range: { from?: Date; to?: Date } | undefined) => {
        const adjustedTo = range?.from === range?.to
            ? new Date(new Date(range?.from || '').setHours(23, 59, 59, 999))
            : range?.to;

        setFilters((prev: any) => ({
            ...prev,
            dateRange: {
                from: range?.from,
                to: adjustedTo
            },
        }));
    };

    const clearDateRange = () => {
        setFilters((prev: any) => ({
            ...prev,
            dateRange: {
                from: undefined,
                to: undefined,
            },
        }));
    };

    const ref = useOutsideClick(() => {
        setIsFiltersShown(false);
    });

    const [_, setSearchParams] = useSearchParams();

    const columnHelper = createColumnHelper();

    const columns = useMemo(
        () => [
            columnHelper.accessor("invoice_data", {
                cell: (info: any) => (
                    <div className="flex gap-10 items-center">
                        <div className="flex py-3 items-center">
                            <span className="font-semibold text-base text-black">
                                <span className="text-[#49484C]">Invoice No:</span> {info?.row?.original?.invoice_number}
                            </span>
                        </div>
                        <div className="h-5 w-0.5 bg-gray-300"></div>
                        <div className="flex py-3 items-center">
                            <span className="font-semibold text-base text-black">FUID No: {info.row.original.fuid}</span>
                        </div>
                    </div>
                ),
            }),
            columnHelper.accessor("expand_toggle", {
                enableSorting: false,
                cell: (info: any) => {
                    const isExpanded = info.row.getIsExpanded();

                    return (
                        <div className="flex justify-end gap-1 pr-1">
                            <button
                                onClick={() => info.row.toggleExpanded()}
                                className="flex items-center justify-center w-8 h-8 p-1.5 border-2 border-[#D0D0D0] rounded-md cursor-pointer bg-[#F1F1F1] shadow-sm"
                            >
                                <img
                                    src={arrowDown}
                                    alt="Expand/Collapse"
                                    className={`w-5 h-5 duration-300 transition-transform ease-in-out ${isExpanded ? "-rotate-180" : "rotate-0"}`}
                                />
                            </button>
                        </div>
                    );
                },
            }),
        ],
        []
    );


    const fetchDetails = useCallback(async () => {
        setLoading(true);

        // Set search params
        setSearchParams({
            pageSize: pageSize.toString(),
            pageIndex: pageIndex.toString(),
        });

        try {
            const hasSearch = globalSearch.trim();
            const isFilterEmpty = !filters ||
                (filters.orderBy?.length === 0 && filters.status?.length === 0 && !filters.dateRange?.from);
            const query = {
                page: pageIndex,
                page_size: pageSize,
                ...(hasSearch ? { [searchCategory]: globalSearch.trim() } : {}),
                ...(filters?.orderBy?.[0]?.key ? { sort: filters.orderBy[0].key } : {}),
                ...(filters?.status?.length ? { status: filters.status.map((item: any) => item?.key) } : {}),
                ...(filters?.company?.length ? { company_code: filters.company.map((item: any) => item?.code).join(',') } : {}),
                ...(filters?.remarkType?.length ? { remark_type: filters.remarkType.map((item: any) => item?.id).join(',') } : {}),
                ...(filters?.dateRange ? { dateRange: filters.dateRange } : {}),
                ...(!globalSearch.trim() && isFilterEmpty ? { status: ['pending'] } : {}),
            };

            const response = await getMasterEscalationList(query);
            const simplified = response?.data?.data.map((item: any) => ({
                id: item.id,
                stockist_id: item.stockist_id,
                counter_code: item.counter_code,
                entry_type: item.entry_type,
                link_type: item.link_type,
                pharmacist_code: item.link_type === 'DRAPH' ? item.pharmacist_code : item.counter_code,
                fuid: item.fuid.fuid_no ? item.fuid.fuid_no : item.fuid,
                fuid_path: item.fuid_path,
                comments: item.comments,
                escalated_by: item.escalated_by,
                escalated_date: item.escalated_date,
                status: item.status,
                invoice_number: item.invoice_number,
                remark_type: item?.remark_type?.reason_desc,
                created_by: item.created_by,
                modified_by: item.modified_by,
                updated_by: item.updated_by
            }));
            setTableData(simplified || []);
            setTotalRowCount(response?.data?.total || 0);
        } catch (error) {
            console.error('Failed to fetch orders:', error);
        } finally {
            setLoading(false);
        }
    }, [
        pageSize,
        pageIndex,
        globalSearch,
        filters?.orderBy,
        filters?.status,
        filters?.dateRange,
        filters?.company,
        filters?.remarkType
    ]);


    const pageCount = Math.ceil(totalRowCount / (pageSize || 1));

    // Debounced Search Handler
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    const handleSearch = (searchQuery: string) => {
        if (timerRef.current) clearTimeout(timerRef.current);

        timerRef.current = setTimeout(() => {
            resetPagination();
            setGlobalSearch(searchQuery);
        }, 500);
    };

    function selectedRowHandler(data: any): void {
        const selectedIds: any[] = [];

        data?.forEach((item: any) => {
            selectedIds.push(item?.original);
        });
    }
    const fetchCompany = async () => {
        try {
            const res = await getAllCompanyDetails();
            setCompanyDetails(res?.data)
        } catch (error) {
            console.log(error)
        }
    }
    const getRemarkHeaderDetails = async () => {
        try {
            const res = await getRemarkHeader()
            setRemarkHdr(res?.data?.data)
        } catch (error) {
            console.log(error)
        }
    }

    // Re-fetch when globalSearch changes
    useEffect(() => {
        fetchDetails();
        fetchCompany();
        getRemarkHeaderDetails();
    }, [globalSearch, fetchDetails]);

    // Divider component
    const Divider = () => <div className="w-full h-[1px] my-2 bg-[#0000001a]" />;

    const activeFilterCount = [filters.status.length > 0, filters.orderBy.length > 0, !!filters.dateRange.from, filters.company.length > 0, filters.remarkType.length > 0].filter(Boolean).length;

    return (
        <Fragment>
            <Transition
                as={Fragment}
                show={isFiltersShown}
                enter="transition transition ease-in-out duration-200 transform"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transition  ease-in-out duration-200 transform"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
            >
                <div className="fixed right-0 inset-0 top-0 z-[500]">
                    <aside
                        className="bg-white border-l-[1px] min-w-[20rem] overflow-y-auto max-w-[25rem] border-l-[#00000014] fixed px-8 text-[#49484C] text-xl font-semibold py-5 right-0  h-screen"
                        ref={ref}
                    >
                        <div className="flex items-center justify-between gap-5 mt-3">
                            <h3 className="whitespace-nowrap">Fetch filters</h3>
                            <Button className="border py-1 bg-[#7F56D9] text-white" onClick={() => {
                                setFilters({
                                    orderBy: "",
                                    status: [],
                                    company: "",
                                    remarkType: '',
                                    dateRange: { from: undefined, to: undefined },
                                })
                                setIsFiltersShown(false)
                            }
                            }>
                                Reset
                            </Button>
                        </div>

                        {/* filters  */}
                        <div className="flex flex-col ">
                            <FilterAccordion
                                uniqueKey="key"
                                displayKey="value"
                                onSelectingFilter={(value) => setFilters((prev: any) => ({ ...prev, orderBy: value }))}
                                title="Sort By"
                                arrayToFilter={SORT_OPTIONS}
                                defaultValue={filters.orderBy}
                            />
                            <Divider />
                            <FilterAccordion
                                uniqueKey="key"
                                displayKey="value"
                                title="Status"
                                arrayToFilter={STATUS_OPTIONS}
                                onSelectingFilter={(value) => setFilters((prev: any) => ({ ...prev, status: value }))}
                                defaultValue={filters.status}
                            />
                            <Divider />
                            <FilterAccordion
                                uniqueKey="code"
                                displayKey="shortname"
                                onSelectingFilter={(value) => setFilters((prev: any) => ({ ...prev, company: value }))}
                                title="Company Name"
                                arrayToFilter={companyDetails}
                                defaultValue={filters.company}
                            />
                            <Divider />
                            <FilterAccordion
                                uniqueKey="id"
                                displayKey="reason_desc"
                                onSelectingFilter={(value) => setFilters((prev: any) => ({ ...prev, remarkType: value }))}
                                title="Remark Type"
                                arrayToFilter={remarkHdr}
                                defaultValue={filters.remarkType}
                            />
                            <Divider />
                            <FilterAccordion
                                displayKey=""
                                title="Date & Time"
                                arrayToFilter={[]}
                                uniqueKey=""
                                onSelectingFilter={() => { }}
                                defaultValue={[]}
                            />
                            <div className="my-0">
                                <div className="flex items-center gap-2 mb-2">
                                    <Button
                                        className="bg-[#7F56D9] text-white w-[10rem]"
                                        onClick={clearDateRange}
                                    >
                                        Clear Date
                                    </Button>
                                </div>

                                <DayPicker
                                    mode="range"
                                    defaultMonth={filters.dateRange.from || undefined}
                                    selected={filters.dateRange}
                                    onSelect={handleDateRangeChange}
                                    numberOfMonths={1}
                                    classNames={{
                                        today: `border border-[#8051FF]`,
                                        selected: `bg-[#ded9ff] border-[#8051FF] font-semibold`,
                                        root: `border border-[#D0D0D0] rounded-xl p-5 flex items-center justify-center text-[1rem]`,
                                        day: `p-2 cursor-pointer`,
                                        chevron: `fill-[#7F56D9] hover:fill-[#8051FF] transition-colors`,
                                        outside: `text-gray-400`,
                                    }}
                                />

                            </div>

                        </div>
                    </aside>
                </div>
            </Transition>
                <div className="flex justify-end float-right gap-2">

                    <div className="inline-flex items-center gap-2">
                        <div className="flex items-center w-[400px] rounded-md overflow-hidden border border-gray-300 bg-white">
                            <Select value={searchCategory} onValueChange={setSearchCategory}>
                                <SelectTrigger className="w-[160px] rounded-none border-none bg-white text-gray-700 focus:ring-0 focus:outline-none focus:shadow-none">
                                    <SelectValue placeholder="Search By" />
                                </SelectTrigger>
                                <SelectContent className="bg-white">
                                    {searchOptionsEscalate.map((option) => (
                                        <SelectItem key={option.value} value={option.value}>
                                            {option.label}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Input
                                placeholder="Search"
                                debounceTime={1250} //M10-11601
                                value={globalSearch}
                                onChange={(e) => handleSearch(e.target.value)}
                                containerClasses="w-full px-3 py-1 text-gray-700 border-none bg-white focus:outline-none focus:ring-0 focus:shadow-none"
                            />
                        </div>
                    </div>
                    <div className="flex items-center gap-2 h-full relative">
                        {activeFilterCount > 0 && (
                            <span className="absolute top-[-10px] right-[-10px] w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white text-xs">
                                {activeFilterCount}
                            </span>
                        )}
                        <Button className="bg-[#F1F1F1]" onClick={() => setIsFiltersShown(true)}>
                            <img src={filterIcon} alt="filter-icon" />
                        </Button>
                    </div>

                </div>

            <div>
                <Table1
                    title="Invoice Details"
                    subtitle="View and manage escalated invoices requests"
                    showheader={false}
                    isLoading={loading}
                    renderSubComponent={(row) => {
                        return (
                            <div className="transition transition ease-in-out duration-200 transform">
                                <InvoiceApprovalDetails resetPagination={resetPagination} row={row} onRefresh={fetchDetails} company_code={filters?.company} />
                            </div>
                        );
                    }}
                    getRowCanExpand={() => true}
                    data={tableData ?? []}
                    columns={columns}
                    showSearch={false}
                    manualPagination={true}
                    pageCount={pageCount}
                    pagination={pagination}
                    onPaginationChange={onPaginationChange}
                    getSelectedRows={selectedRowHandler}
                />
            </div>
        </Fragment>
    );
};

export default EscalatedMasterTab;

